import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function Contactos() {
  const [contactos, setContactos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterLeido, setFilterLeido] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedContacto, setSelectedContacto] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [filterTipo, setFilterTipo] = useState('') // 'marca' | 'postulacion' | ''

  // Estado para dropdown de exportar
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    fetchContactos()
  }, [])

  // Cerrar dropdown al clickear fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchContactos = async () => {
    try {
      setLoading(true)
      const { data: marcas, error: errorMarcas } = await supabase
        .from('contacto_marcas')
        .select('*')
        .order('created_at', { ascending: false })

      if (errorMarcas) throw errorMarcas

      const { data: postulaciones, error: errorPost } = await supabase
        .from('postulaciones')
        .select('*')
        .order('created_at', { ascending: false })

      if (errorPost) throw errorPost

      // Normalize both data sources into a unified structure
      const normalized = [
        ...(marcas || []).map(m => ({ ...m, tipo: 'marca' })),
        ...(postulaciones || []).map(p => ({
          id: p.id,
          tipo: 'postulacion',
          nombre_empresa: null,
          nombre_contacto: p.nombres,
          ruc: null,
          email: p.email,
          telefono: p.telefono,
          mensaje: null,
          cargo: p.cargo,
          cv_path: p.cv_path,
          leido: p.leido,
          created_at: p.created_at
        }))
      ]

      // Sort by date descending
      normalized.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

      setContactos(normalized)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleLeido = async (id, currentState, tipo) => {
    const table = tipo === 'postulacion' ? 'postulaciones' : 'contacto_marcas'
    const { error } = await supabase
      .from(table)
      .update({ leido: !currentState })
      .eq('id', id)

    if (!error) {
      fetchContactos()
    }
  }

  const handleDelete = async (id, tipo) => {
    if (!confirm('¿Estás seguro de eliminar este contacto?')) return

    const table = tipo === 'postulacion' ? 'postulaciones' : 'contacto_marcas'
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id)

    if (!error) {
      fetchContactos()
      alert('Contacto eliminado exitosamente')
    }
  }

  const exportMarcasCSV = async () => {
    const { data: marcas } = await supabase
      .from('contacto_marcas')
      .select('*')
      .order('created_at', { ascending: false })

    if (!marcas || marcas.length === 0) {
      alert('No hay datos de Marcas para exportar')
      return
    }

    const headers = [
      'Empresa',
      'Contacto',
      'RUC',
      'Email',
      'Teléfono',
      'Mensaje',
      'Estado',
      'Fecha Creación'
    ]

    const rows = marcas.map(m => [
      m.nombre_empresa || '',
      m.nombre_contacto || '',
      m.ruc || '',
      m.email || '',
      m.telefono || '',
      m.mensaje || '',
      m.leido ? 'Leído' : 'No leído',
      new Date(m.created_at).toLocaleString('es-PE')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `contacto_marcas_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    setShowExportDropdown(false)
  }

  const exportPostulacionesCSV = async () => {
    const { data: postulaciones } = await supabase
      .from('postulaciones')
      .select('*')
      .order('created_at', { ascending: false })

    if (!postulaciones || postulaciones.length === 0) {
      alert('No hay datos de Postulaciones para exportar')
      return
    }

    const headers = [
      'Nombres',
      'Email',
      'Teléfono',
      'Cargo',
      'CV Path',
      'CV Tamaño (bytes)',
      'Origen',
      'Estado',
      'Fecha Creación'
    ]

    const rows = postulaciones.map(p => [
      p.nombres || '',
      p.email || '',
      p.telefono || '',
      p.cargo || '',
      p.cv_path || '',
      p.cv_size_bytes || '',
      p.origen || '',
      p.leido ? 'Leído' : 'No leído',
      new Date(p.created_at).toLocaleString('es-PE')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `postulaciones_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    setShowExportDropdown(false)
  }

  const filteredContactos = contactos.filter(contacto => {
    const baseText = [
      contacto.nombre_empresa || '',
      contacto.nombre_contacto || '',
      contacto.email || '',
      contacto.cargo || ''
    ].join(' ').toLowerCase()

    const matchesSearch = baseText.includes(searchTerm.toLowerCase())
    
    const matchesFilter = 
      filterLeido === '' ||
      (filterLeido === 'leido' && contacto.leido) ||
      (filterLeido === 'no_leido' && !contacto.leido)

    const matchesTipo =
      filterTipo === '' || contacto.tipo === filterTipo

    return matchesSearch && matchesFilter && matchesTipo
  })

  const contactosNoLeidos = contactos.filter(c => !c.leido).length

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Contactos y Postulaciones</h1>
          <p className="text-gray-600 mt-1">
            {contactosNoLeidos} contacto{contactosNoLeidos !== 1 ? 's' : ''} sin leer
          </p>
        </div>
        
        {/* Dropdown Exportar CSV */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowExportDropdown(!showExportDropdown)}
            disabled={contactos.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            📄 Exportar CSV
            <svg 
              className={`w-4 h-4 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showExportDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border z-10">
              <button
                onClick={exportMarcasCSV}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b"
              >
                <span className="text-xl">🏢</span>
                <div>
                  <p className="font-medium text-sm">Contacto de Marcas</p>
                  <p className="text-xs text-gray-500">{contactos.filter(c => c.tipo === 'marca').length} registros</p>
                </div>
              </button>
              
              <button
                onClick={exportPostulacionesCSV}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
              >
                <span className="text-xl">👤</span>
                <div>
                  <p className="font-medium text-sm">Postulaciones</p>
                  <p className="text-xs text-gray-500">{contactos.filter(c => c.tipo === 'postulacion').length} registros</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Buscar por empresa, contacto, cargo o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          />
          <select
            value={filterLeido}
            onChange={(e) => setFilterLeido(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos los estados</option>
            <option value="no_leido">No leídos</option>
            <option value="leido">Leídos</option>
          </select>
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos los tipos</option>
            <option value="marca">Marcas</option>
            <option value="postulacion">Postulaciones</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total Contactos</p>
          <p className="text-2xl font-bold text-primary">{contactos.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">No Leídos</p>
          <p className="text-2xl font-bold text-orange-600">{contactosNoLeidos}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Leídos</p>
          <p className="text-2xl font-bold text-green-600">{contactos.length - contactosNoLeidos}</p>
        </div>
      </div>

      {/* Lista de Contactos */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="text-center py-12"><LoadingSpinner message="" size={48} /></div>
        ) : filteredContactos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No se encontraron contactos
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredContactos.map((contacto) => (
              <div
                key={contacto.id}
                className={`p-6 hover:bg-gray-50 transition-colors ${
                  !contacto.leido ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {contacto.tipo === 'postulacion'
                          ? contacto.nombre_contacto
                          : contacto.nombre_empresa}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        contacto.tipo === 'postulacion' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {contacto.tipo === 'postulacion' ? 'POSTULACIÓN' : 'MARCA'}
                      </span>
                      {!contacto.leido && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded">
                          NUEVO
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3">
                      {contacto.tipo === 'marca' ? (
                        <>
                          <p className="text-gray-700">
                            <span className="font-medium">Contacto:</span> {contacto.nombre_contacto}
                          </p>
                          <p className="text-gray-700">
                            <span className="font-medium">Email:</span> {contacto.email}
                          </p>
                          {contacto.telefono && (
                            <p className="text-gray-700">
                              <span className="font-medium">Teléfono:</span> {contacto.telefono}
                            </p>
                          )}
                          {contacto.ruc && (
                            <p className="text-gray-700">
                              <span className="font-medium">RUC:</span> {contacto.ruc}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-gray-700">
                            <span className="font-medium">Cargo:</span> {contacto.cargo}
                          </p>
                          <p className="text-gray-700">
                            <span className="font-medium">Email:</span> {contacto.email}
                          </p>
                          {contacto.telefono && (
                            <p className="text-gray-700">
                              <span className="font-medium">Teléfono:</span> {contacto.telefono}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Fecha:</span>{' '}
                      {new Date(contacto.created_at).toLocaleDateString('es-PE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>

                    <button
                      onClick={() => {
                        setSelectedContacto(contacto)
                        setShowModal(true)
                      }}
                      className="text-sm text-primary hover:underline"
                    >
                      Ver {contacto.tipo === 'postulacion' ? 'postulación completa' : 'mensaje completo'} →
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => toggleLeido(contacto.id, contacto.leido, contacto.tipo)}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        contacto.leido
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {contacto.leido ? '✓ Leído' : 'Marcar leído'}
                    </button>
                    <button
                      onClick={() => handleDelete(contacto.id, contacto.tipo)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Detalle */}
      {showModal && selectedContacto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-h-[85vh] overflow-hidden" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '672px' }}>
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">
                  {selectedContacto.tipo === 'postulacion'
                    ? selectedContacto.nombre_contacto
                    : selectedContacto.nombre_empresa}
                </h2>
                <p className="text-gray-600 mt-1">
                  {selectedContacto.tipo === 'postulacion' ? 'Postulación laboral' : 'Contacto de marca'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-200px)]">
              <div className="space-y-4">
                {selectedContacto.tipo === 'marca' ? (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Empresa</p>
                      <p className="text-gray-900 font-semibold">{selectedContacto.nombre_empresa}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-600">Persona de contacto</p>
                      <p className="text-gray-900">{selectedContacto.nombre_contacto}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Email</p>
                        <p className="text-gray-900">{selectedContacto.email}</p>
                      </div>
                      {selectedContacto.telefono && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Teléfono</p>
                          <p className="text-gray-900">{selectedContacto.telefono}</p>
                        </div>
                      )}
                    </div>

                    {selectedContacto.ruc && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">RUC</p>
                        <p className="text-gray-900">{selectedContacto.ruc}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Mensaje</p>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-900 whitespace-pre-line">{selectedContacto.mensaje}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Nombre completo</p>
                      <p className="text-gray-900 font-semibold">{selectedContacto.nombre_contacto}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Email</p>
                        <p className="text-gray-900">{selectedContacto.email}</p>
                      </div>
                      {selectedContacto.telefono && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Teléfono</p>
                          <p className="text-gray-900">{selectedContacto.telefono}</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-600">Cargo al que postula</p>
                      <p className="text-gray-900">{selectedContacto.cargo}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Currículum Vitae</p>
                      <div className="space-y-2">
                        <button
                          onClick={async () => {
                            try {
                              const { data, error } = await supabase
                                .storage
                                .from('postulaciones')
                                .createSignedUrl(selectedContacto.cv_path, 300)
                              if (error) throw error
                              window.open(data.signedUrl, '_blank')
                            } catch (err) {
                              alert('No se pudo generar el enlace del CV')
                              console.error(err)
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          type="button"
                        >
                          📄 Ver CV (PDF)
                        </button>
                        <div className="text-xs text-gray-500 space-y-1">
                          <p><span className="font-medium">Archivo:</span> {selectedContacto.cv_path?.split('/').pop() || 'cv.pdf'}</p>
                          {selectedContacto.cv_size_bytes && (
                            <p><span className="font-medium">Tamaño:</span> {(selectedContacto.cv_size_bytes / 1024).toFixed(2)} KB</p>
                          )}
                          {selectedContacto.origen && (
                            <p><span className="font-medium">Origen:</span> {selectedContacto.origen}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <p className="text-sm font-medium text-gray-600">Fecha de envío</p>
                  <p className="text-gray-900">
                    {new Date(selectedContacto.created_at).toLocaleDateString('es-PE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              {!selectedContacto.leido && (
                <button
                  onClick={() => {
                    toggleLeido(selectedContacto.id, selectedContacto.leido, selectedContacto.tipo)
                    setShowModal(false)
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Marcar como leído
                </button>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
